import { useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import type {
  AdminTournamentBracket,
  AdminTournamentBracketMatch,
  AdminTournamentSchedule,
  AdminTournamentScheduleSlot,
} from "../services/eventRegistrationAdminService";
import {
  createScheduleDraft,
  isByeMatch,
  isGroupStageMatch,
  manualGroupId,
  manualGroupName,
  roundLabel,
  schedulePayloadFromDraft,
  slotMatchId,
  stablePayload,
  timelineStatusValue,
  toDateTimeLocal,
  type ScheduleDraft,
  type TournamentBracketBlock,
} from "../utils/tournamentOperations";

type ManualGroupAssignments = Record<string, string>;

type UseAdminTournamentDerivedDataParams = {
  bracket: AdminTournamentBracket | null;
  categoryKey: string;
  manualGroupCount: string;
  schedule: AdminTournamentSchedule | null;
  scheduleDraft: ScheduleDraft;
  setManualAssignments: Dispatch<SetStateAction<ManualGroupAssignments>>;
  timelineCategoryFilter: string;
  timelineDayFilter: string;
  timelineStatusFilter: string;
};

export function useAdminTournamentDerivedData({
  bracket,
  categoryKey,
  manualGroupCount,
  schedule,
  scheduleDraft,
  setManualAssignments,
  timelineCategoryFilter,
  timelineDayFilter,
  timelineStatusFilter,
}: UseAdminTournamentDerivedDataParams) {
  const scheduledByMatchId = useMemo(() => {
    const map = new Map<number, AdminTournamentScheduleSlot>();
    (schedule?.slots || []).forEach((slot) => {
      const matchId = slotMatchId(slot);
      if (matchId > 0) map.set(matchId, slot);
    });
    return map;
  }, [schedule?.slots]);

  const bracketMatchById = useMemo(() => {
    const map = new Map<number, AdminTournamentBracketMatch>();
    (bracket?.rounds || []).forEach((round) => {
      round.matches.forEach((match) => {
        const matchId = Number(match.id || 0);
        if (matchId > 0) map.set(matchId, match);
      });
    });
    return map;
  }, [bracket]);

  const bracketBlocks = useMemo<TournamentBracketBlock[]>(() => {
    if (!bracket) return [];
    const allMatches = bracket.rounds
      .flatMap((round) => round.matches)
      .sort((left, right) => {
        if (left.roundNumber !== right.roundNumber) {
          return Number(left.roundNumber || 0) - Number(right.roundNumber || 0);
        }
        return Number(left.matchNumber || 0) - Number(right.matchNumber || 0);
      });
    const consumedMatchIds = new Set<number>();
    const blocks: TournamentBracketBlock[] = [];

    (bracket.metadata?.groups || []).forEach((group) => {
      const groupId = String(group.id || "").trim();
      const groupName = String(group.name || groupId || "Grupo").trim();
      const matches = allMatches.filter((match) => {
        const matchGroupKey = String(match.groupKey || "").trim();
        const matchGroupName = String(match.groupName || "").trim();
        return (
          isGroupStageMatch(match) &&
          (matchGroupKey === groupId || matchGroupName === groupName)
        );
      });
      matches.forEach((match) => consumedMatchIds.add(Number(match.id)));
      blocks.push({
        id: `group-${groupId || groupName}`,
        title: groupName,
        subtitle: "Fase de grupos",
        kind: "group",
        isSeedHead: Boolean(group.isSeedHead),
        teams: group.teams
          .map((team) => ({
            registrationId: Number(team.registrationId || 0),
            label: String(team.label || "").trim(),
            isSeedHead: Boolean(team.isSeedHead),
            seedRank: team.seedRank ?? null,
          }))
          .filter((team) => Boolean(team.label)),
        matches: matches.filter((match) => !isByeMatch(match)),
      });
    });

    const remainingByRound = new Map<string, TournamentBracketBlock>();
    allMatches
      .filter((match) => !consumedMatchIds.has(Number(match.id)))
      .forEach((match) => {
        if (isByeMatch(match)) return;
        const key = `round-${match.roundNumber}-${match.roundCode}`;
        if (!remainingByRound.has(key)) {
          remainingByRound.set(key, {
            id: key,
            title: roundLabel(match.roundCode, match.roundNumber),
            subtitle: "Mata-mata",
            kind: "round",
            teams: [],
            matches: [],
          });
        }
        remainingByRound.get(key)?.matches.push(match);
      });

    return [...blocks, ...Array.from(remainingByRound.values())].filter(
      (block) => block.teams.length > 0 || block.matches.length > 0,
    );
  }, [bracket]);

  const groupBracketBlocks = useMemo(
    () => bracketBlocks.filter((block) => block.kind === "group"),
    [bracketBlocks],
  );

  const roundBracketBlocks = useMemo(
    () => bracketBlocks.filter((block) => block.kind !== "group"),
    [bracketBlocks],
  );

  const firstKnockoutRoundNumber = useMemo(() => {
    const roundNumbers = (bracket?.rounds || [])
      .flatMap((round) => round.matches || [])
      .filter(
        (match) => String(match.stage || "").trim().toLowerCase() === "knockout",
      )
      .map((match) => Number(match.roundNumber || 0))
      .filter((value) => Number.isFinite(value) && value > 0);
    return roundNumbers.length > 0 ? Math.min(...roundNumbers) : 0;
  }, [bracket]);

  const dayOptions = useMemo(() => {
    const days = new Set<string>();
    (schedule?.slots || []).forEach((slot) => {
      const day = toDateTimeLocal(slot.slotStartAt).slice(0, 10);
      if (day) days.add(day);
    });
    return Array.from(days).sort();
  }, [schedule?.slots]);

  const visibleTimelineSlots = useMemo(() => {
    return [...(schedule?.slots || [])]
      .filter((slot) => {
        if (!slotMatchId(slot)) return false;
        if (
          timelineCategoryFilter !== "all" &&
          String(slot.categoryKey || "").trim() !== timelineCategoryFilter
        ) {
          return false;
        }
        if (
          timelineDayFilter !== "all" &&
          toDateTimeLocal(slot.slotStartAt).slice(0, 10) !== timelineDayFilter
        ) {
          return false;
        }
        const status = timelineStatusValue(slot);
        if (timelineStatusFilter !== "all" && status !== timelineStatusFilter) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const timeDiff =
          new Date(a.slotStartAt).getTime() - new Date(b.slotStartAt).getTime();
        if (timeDiff !== 0) return timeDiff;
        return Number(a.id || 0) - Number(b.id || 0);
      });
  }, [
    schedule?.slots,
    timelineCategoryFilter,
    timelineDayFilter,
    timelineStatusFilter,
  ]);

  const courtNames = useMemo(() => {
    const fromConfig = schedule?.config?.courtNames || [];
    const fromDraft = scheduleDraft.courtNames
      .split(/\r?\n/)
      .map((name) => name.trim())
      .filter(Boolean);
    return fromConfig.length ? fromConfig : fromDraft;
  }, [schedule?.config?.courtNames, scheduleDraft.courtNames]);

  const manualGroupOptions = useMemo(() => {
    const count = Math.max(1, Math.min(16, Number(manualGroupCount || 1)));
    return Array.from({ length: count }, (_item, index) => ({
      id: manualGroupId(index),
      name: manualGroupName(index),
    }));
  }, [manualGroupCount]);

  useEffect(() => {
    setManualAssignments((current) => {
      const allowed = new Set(manualGroupOptions.map((group) => group.id));
      const fallback = manualGroupOptions[0]?.id || "a";
      return Object.fromEntries(
        Object.entries(current).map(([registrationId, groupId]) => [
          registrationId,
          allowed.has(groupId) ? groupId : fallback,
        ]),
      );
    });
  }, [manualGroupOptions, setManualAssignments]);

  const persistedScheduleDraft = useMemo(
    () => createScheduleDraft(schedule?.config),
    [schedule?.config],
  );
  const scheduleChanged = useMemo(
    () =>
      stablePayload(schedulePayloadFromDraft(scheduleDraft)) !==
      stablePayload(schedulePayloadFromDraft(persistedScheduleDraft)),
    [persistedScheduleDraft, scheduleDraft],
  );
  const scheduleConfigurationMissing = !schedule?.config?.updatedAt;
  const pendingOperationLabels = useMemo(
    () => [
      ...(scheduleChanged || scheduleConfigurationMissing
        ? ["programação"]
        : []),
    ],
    [scheduleChanged, scheduleConfigurationMissing],
  );
  const hasPendingOperationChanges = pendingOperationLabels.length > 0;
  const currentSchedulePayload = useMemo(
    () => schedulePayloadFromDraft(scheduleDraft),
    [scheduleDraft],
  );
  const tournamentConfigurationBlocker = useMemo(() => {
    if (!categoryKey) return "Selecione uma categoria antes de gerar a chave.";
    if (!currentSchedulePayload) {
      return "Complete as configurações do torneio antes de gerar a chave.";
    }
    if (scheduleConfigurationMissing) {
      return "Complete e salve as configurações do torneio antes de gerar a chave.";
    }
    if (hasPendingOperationChanges) {
      return `Salve as alterações pendentes (${pendingOperationLabels.join(" e ")}) antes de gerar a chave.`;
    }
    return "";
  }, [
    categoryKey,
    currentSchedulePayload,
    hasPendingOperationChanges,
    pendingOperationLabels,
    scheduleConfigurationMissing,
  ]);

  return {
    bracketMatchById,
    courtNames,
    currentSchedulePayload,
    dayOptions,
    firstKnockoutRoundNumber,
    groupBracketBlocks,
    hasPendingOperationChanges,
    manualGroupOptions,
    pendingOperationLabels,
    roundBracketBlocks,
    scheduleChanged,
    scheduleConfigurationMissing,
    scheduledByMatchId,
    tournamentConfigurationBlocker,
    visibleTimelineSlots,
  };
}
